import { Badge, Box, Button, Circle, Flex, Icon, Text } from '@chakra-ui/react'
import { useSetAtom } from 'jotai'
import { Download, Globe, MessageSquare, Phone } from 'lucide-react'
import React, { useEffect, useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAnnounce } from '~/components/shared/LiveAnnouncer'
import { useAnalytics } from '~/hooks/useAnalytics'
import { connectionRefreshRequestedAtom } from '~/hooks/usePhone'
import { TranslationKeys } from '~/locales/i18next'
import { useElectronUpdaterContext } from '~/providers/ElectronUpdaterContext'
import { AnalyticsNamespace, WebPhoneAnalyticsEvents } from '~/types/Analytics'
import { openInNewTab } from '~/utils/functions'

import {
  BADGE_PALETTE,
  HEALTH_COLOR,
  type HealthRow,
  IN_PROGRESS_LABEL_KEYS,
  networkHealth,
  OVERALL_LABEL_FALLBACK,
  OVERALL_LABEL_KEY,
  overallHealth,
  phoneServiceHealth,
  realtimeHealth,
} from './statusHealth'
import { useAppUpdatesHealth } from './useAppUpdatesHealth'
import { useDiagnosticsSnapshot } from './useDiagnosticsSnapshot'

type RowAction = {
  label: string
  onClick: () => void
  /** `ghost` for optional actions on a healthy row; defaults to `outline`. */
  variant?: 'outline' | 'ghost'
  /** The action changes the row's state, so the resulting status is announced. */
  announcesResult?: boolean
}

type RowProps = {
  icon: React.ComponentType<any>
  title: string
  health: HealthRow
  actions?: RowAction[]
  /**
   * Changes each time the row's work gets an answer, even one that leaves the
   * status unchanged, so the unchanged status can still be announced.
   */
  resultRevision?: number
}

/**
 * Pressing an action can change the row's state and remove the pressed
 * button. Focus then moves to the row's status text. After an action marked
 * `announcesResult`, the row's status is announced each time it changes or
 * `resultRevision` reports an answer, until it settles in a state that isn't
 * in progress. A status that focus just moved to is already read, so it isn't
 * announced again.
 */
const StatusRow = ({
  icon,
  title,
  health,
  actions = [],
  resultRevision = 0,
}: RowProps) => {
  const { t } = useTranslation()
  const announce = useAnnounce()
  const statusText = t(health.labelKey as TranslationKeys, health.labelValues)
  const statusRef = useRef<HTMLParagraphElement>(null)
  const focusedActionRef = useRef<HTMLElement | null>(null)
  const hasUsedActionRef = useRef(false)
  const focusedStatusTextRef = useRef<string | null>(null)

  useLayoutEffect(() => {
    const action = focusedActionRef.current
    if (!action || action.isConnected) return
    focusedActionRef.current = null
    const active = document.activeElement
    if (!active || active === document.body || active === action) {
      focusedStatusTextRef.current = statusText
      statusRef.current?.focus()
    }
  })

  useEffect(() => {
    if (!hasUsedActionRef.current) return
    if (focusedStatusTextRef.current === statusText) {
      focusedStatusTextRef.current = null
    } else {
      announce(statusText)
    }
    if (!IN_PROGRESS_LABEL_KEYS.has(health.labelKey)) {
      hasUsedActionRef.current = false
    }
  }, [statusText, resultRevision, health.labelKey, announce])

  const handleActionFocus = (event: React.FocusEvent<HTMLElement>) => {
    focusedActionRef.current = event.currentTarget
  }
  // A blur without a new target can be the button being removed.
  const handleActionBlur = (event: React.FocusEvent<HTMLElement>) => {
    if (event.relatedTarget) focusedActionRef.current = null
  }

  return (
    <Box
      px={5}
      py={4}
      bg="bg.default"
      borderRadius="lg"
      borderWidth="1px"
      borderColor="border.default"
    >
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={3}>
          <Icon as={icon} boxSize={5} color="fg.muted" />
          <Text fontSize="md" fontWeight="medium" color="fg.default">
            {title}
          </Text>
        </Flex>
        <Flex align="center" gap={2}>
          <Circle size="0.625rem" bg={HEALTH_COLOR[health.level]} />
          <Text ref={statusRef} tabIndex={-1} fontSize="sm" color="fg.muted">
            {statusText}
          </Text>
        </Flex>
      </Flex>
      {health.fixKey && (
        <Text fontSize="sm" color="fg.muted" mt={2} pl={8}>
          {t(health.fixKey as TranslationKeys)}
        </Text>
      )}
      {actions.length > 0 && (
        <Flex mt={3} pl={8} gap={2} wrap="wrap">
          {actions.map((action) => (
            <Button
              key={action.label}
              size="sm"
              variant={action.variant ?? 'outline'}
              onFocus={handleActionFocus}
              onBlur={handleActionBlur}
              onClick={() => {
                if (action.announcesResult) hasUsedActionRef.current = true
                action.onClick()
              }}
            >
              {action.label}
            </Button>
          ))}
        </Flex>
      )}
    </Box>
  )
}

const useAppUpdatesRow = (): {
  health: HealthRow
  actions: RowAction[]
  resultRevision?: number
} | null => {
  const { t } = useTranslation()
  const {
    updatePermissionHelpUrl,
    isUpdateBlocked,
    updateBlockedDownloadUrl,
    isUpdateAvailable,
    isUpdateDownloaded,
    isChecking,
    lastCheckStatus,
    permissionRecheckCount,
    checkForUpdates,
    updateIgnoringInstallPermission,
    quitAndInstall,
  } = useElectronUpdaterContext()
  const health = useAppUpdatesHealth()

  // After a renderer reload (e.g. logout), nothing checks again until the
  // periodic interval, so opening the panel starts the check it reports on.
  // Permission-required and blocked already have an answer and leave the
  // status pending, so they're excluded to avoid a new attempt on every open.
  const needsInitialCheck =
    Boolean(health) &&
    lastCheckStatus === 'pending' &&
    !updatePermissionHelpUrl &&
    !isUpdateBlocked
  useEffect(() => {
    if (needsInitialCheck) checkForUpdates()
  }, [needsInitialCheck])

  if (!health) return null

  if (updatePermissionHelpUrl) {
    return {
      health,
      resultRevision: permissionRecheckCount,
      actions: [
        {
          label: t('updatePermissionBanner.howToFix', 'How to fix'),
          onClick: () => openInNewTab(updatePermissionHelpUrl),
        },
        {
          label: t('statusPanel.checkAgain', 'Check again'),
          onClick: checkForUpdates,
          variant: 'ghost',
          announcesResult: true,
        },
        {
          label: t('updatePermissionBanner.updateAnyway', 'Update anyway'),
          onClick: updateIgnoringInstallPermission,
          variant: 'ghost',
          announcesResult: true,
        },
      ],
    }
  }
  if (isUpdateBlocked) {
    return {
      health,
      actions: updateBlockedDownloadUrl
        ? [
            {
              label: t(
                'updateBlockedBanner.downloadLatest',
                'Download latest version',
              ),
              onClick: () => openInNewTab(updateBlockedDownloadUrl),
            },
          ]
        : [],
    }
  }
  if (isUpdateDownloaded) {
    return {
      health,
      actions: [
        {
          label: t('updateCheck.restartAndUpdate', 'Restart & Update'),
          onClick: quitAndInstall,
          announcesResult: true,
        },
      ],
    }
  }
  if (isChecking || isUpdateAvailable) {
    return { health, actions: [] }
  }
  return {
    health,
    actions: [
      {
        label: t('updateCheck.checkForUpdates', 'Check for updates'),
        onClick: checkForUpdates,
        variant: lastCheckStatus === 'failed' ? 'outline' : 'ghost',
        announcesResult: true,
      },
    ],
  }
}

// Same refresh request the Web Phone's Reconnect button makes; usePhoneHub
// (always mounted) picks it up and re-fetches SIP credentials.
const usePhoneServiceActions = (health: HealthRow): RowAction[] => {
  const { t } = useTranslation()
  const requestConnectionRefresh = useSetAtom(connectionRefreshRequestedAtom)
  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)

  if (health.labelKey !== 'statusPanel.stateRegistrationError') return []

  return [
    {
      label: t('webPhone.callError.reconnect', 'Reconnect'),
      onClick: () => {
        requestConnectionRefresh(true)
        track(WebPhoneAnalyticsEvents.RestartSipClientStatusPanelClick)
      },
      announcesResult: true,
    },
  ]
}

export const StatusBasicView = () => {
  const { t } = useTranslation()
  const snap = useDiagnosticsSnapshot()
  const network = networkHealth(snap)
  const phone = phoneServiceHealth(snap)
  const phoneActions = usePhoneServiceActions(phone)
  const realtime = realtimeHealth(snap)
  const appUpdates = useAppUpdatesRow()
  const overall = overallHealth(
    appUpdates
      ? [network, phone, realtime, appUpdates.health]
      : [network, phone, realtime],
  )
  const isGood = overall === 'healthy' || overall === 'idle'

  return (
    <Box maxW="40rem" mx="auto" w="100%" px={6} py={8}>
      <Flex direction="column" align="center" gap={3} mb={8}>
        <Circle size="3.5rem" bg={`${HEALTH_COLOR[overall]}/15`}>
          <Circle size="1.25rem" bg={HEALTH_COLOR[overall]} />
        </Circle>
        <Badge size="lg" colorPalette={BADGE_PALETTE[overall]}>
          {t(
            OVERALL_LABEL_KEY[overall] as TranslationKeys,
            OVERALL_LABEL_FALLBACK[overall],
          )}
        </Badge>
        <Text fontSize="sm" color="fg.muted" textAlign="center">
          {isGood
            ? t('statusPanel.summaryGood', 'Everything looks good.')
            : t('statusPanel.summaryProblem', 'We found a problem.')}
        </Text>
      </Flex>

      <Flex direction="column" gap={3}>
        <StatusRow
          icon={Globe}
          title={t('statusPanel.rowInternet', 'Internet')}
          health={network}
        />
        <StatusRow
          icon={Phone}
          title={t('statusPanel.rowPhoneService', 'Phone service')}
          health={phone}
          actions={phoneActions}
        />
        <StatusRow
          icon={MessageSquare}
          title={t('statusPanel.rowMessaging', 'Messaging')}
          health={realtime}
        />
        {appUpdates && (
          <StatusRow
            icon={Download}
            title={t('statusPanel.rowAppUpdates', 'App updates')}
            health={appUpdates.health}
            actions={appUpdates.actions}
            resultRevision={appUpdates.resultRevision}
          />
        )}
      </Flex>
    </Box>
  )
}
